import React, { useMemo } from "react";
import { useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { AccountData } from "./AccountSettingData";
import urls from "../../global/constants/UrlConstants";
import history from "../../utils/history";
import { locationType } from "../../Models/Interface";
import { selectPrivileges } from "../../redux/authSlice";
import { canShowAccountSettingCard } from "../../Shared/AppDrawer/menuPrivileges";

interface customProps {
  location: locationType;
}

const ItemIcon = ({ name }: { name: string }) => {
  const common = {
    width: 16,
    height: 16,
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 1.75,
  } as const;

  switch (name) {
    case "building":
      return (
        <svg {...common}>
          <path d="M3 21h18M5 21V7l7-4 7 4v14M9 21v-6h6v6" />
        </svg>
      );
    case "card":
      return (
        <svg {...common}>
          <rect x="2" y="5" width="20" height="14" rx="2" />
          <path d="M2 10h20" />
        </svg>
      );
    case "pin":
      return (
        <svg {...common}>
          <path d="M12 21s7-4.5 7-11a7 7 0 10-14 0c0 6.5 7 11 7 11z" />
          <circle cx="12" cy="10" r="2.5" />
        </svg>
      );
    case "users":
      return (
        <svg {...common}>
          <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" />
          <circle cx="9" cy="7" r="4" />
          <path d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75" />
        </svg>
      );
    case "user":
      return (
        <svg {...common}>
          <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" />
          <circle cx="12" cy="7" r="4" />
        </svg>
      );
    case "mail":
      return (
        <svg {...common}>
          <rect x="2" y="4" width="20" height="16" rx="2" />
          <path d="M22 7l-10 7L2 7" />
        </svg>
      );
    case "bell":
      return (
        <svg {...common}>
          <path d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9" />
          <path d="M13.73 21a2 2 0 01-3.46 0" />
        </svg>
      );
    case "globe":
      return (
        <svg {...common}>
          <circle cx="12" cy="12" r="9" />
          <path d="M3 12h18M12 3a15 15 0 010 18M12 3a15 15 0 000 18" />
        </svg>
      );
    case "lock":
      return (
        <svg {...common}>
          <rect x="4" y="11" width="16" height="10" rx="2" />
          <path d="M8 11V7a4 4 0 118 0v4" />
        </svg>
      );
    case "shield":
      return (
        <svg {...common}>
          <path d="M12 3l8 4v5c0 5-3.5 8.5-8 10-4.5-1.5-8-5-8-10V7l8-4z" />
        </svg>
      );
    case "clock":
      return (
        <svg {...common}>
          <circle cx="12" cy="12" r="9" />
          <path d="M12 7v5l3 2" />
        </svg>
      );
    case "monitor":
      return (
        <svg {...common}>
          <rect x="2" y="3" width="20" height="14" rx="2" />
          <path d="M8 21h8M12 17v4" />
        </svg>
      );
    case "badge":
      return (
        <svg {...common}>
          <circle cx="12" cy="8" r="4" />
          <path d="M6 21v-2a4 4 0 014-4h4a4 4 0 014 4v2" />
        </svg>
      );
    case "key":
      return (
        <svg {...common}>
          <circle cx="8" cy="15" r="4" />
          <path d="M11.5 12.5L21 3M17 3h4v4" />
        </svg>
      );
    case "sliders":
      return (
        <svg {...common}>
          <path d="M4 21v-7M4 10V3M12 21v-9M12 8V3M20 21v-5M20 12V3M1 14h6M9 8h6M17 16h6" />
        </svg>
      );
    case "list":
      return (
        <svg {...common}>
          <path d="M8 6h13M8 12h13M8 18h13M3 6h.01M3 12h.01M3 18h.01" />
        </svg>
      );
    case "plug":
      return (
        <svg {...common}>
          <path d="M12 22v-5M9 8V2M15 8V2M8 8h8v4a4 4 0 01-8 0V8z" />
        </svg>
      );
    case "code":
      return (
        <svg {...common}>
          <path d="M16 18l6-6-6-6M8 6l-6 6 6 6" />
        </svg>
      );
    case "database":
      return (
        <svg {...common}>
          <ellipse cx="12" cy="5" rx="8" ry="3" />
          <path d="M4 5v6c0 1.7 3.6 3 8 3s8-1.3 8-3V5M4 11v6c0 1.7 3.6 3 8 3s8-1.3 8-3v-6" />
        </svg>
      );
    case "target":
      return (
        <svg {...common}>
          <circle cx="12" cy="12" r="9" />
          <circle cx="12" cy="12" r="5" />
          <circle cx="12" cy="12" r="1.5" />
        </svg>
      );
    case "zap":
      return (
        <svg {...common}>
          <path d="M13 2L3 14h8l-1 8 10-12h-8l1-8z" />
        </svg>
      );
    case "funnel":
      return (
        <svg {...common}>
          <path d="M3 4h18l-7 8v6l-4 2v-8L3 4z" />
        </svg>
      );
    default:
      return (
        <svg {...common}>
          <circle cx="12" cy="12" r="3" />
        </svg>
      );
  }
};

const CardHeaderIcon = ({
  type,
  color,
}: {
  type: string;
  color: string;
}) => {
  const common = {
    width: 22,
    height: 22,
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: color,
    strokeWidth: 1.8,
  } as const;

  switch (type) {
    case "Organization Setup":
      return (
        <svg {...common}>
          <path d="M3 21h18M5 21V7l7-4 7 4v14M9 9h.01M15 9h.01M9 13h.01M15 13h.01M9 17h.01M15 17h.01" />
        </svg>
      );
    case "Profile":
      return (
        <svg {...common}>
          <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" />
          <circle cx="12" cy="7" r="4" />
        </svg>
      );
    case "Security":
      return (
        <svg {...common}>
          <path d="M12 3l8 4v5c0 5-3.5 8.5-8 10-4.5-1.5-8-5-8-10V7l8-4z" />
          <path d="M9 12l2 2 4-4" />
        </svg>
      );
    case "Role":
      return (
        <svg {...common}>
          <circle cx="9" cy="8" r="3.5" />
          <path d="M2 20v-1.5A4.5 4.5 0 016.5 14h5A4.5 4.5 0 0116 18.5V20" />
          <path d="M17 8h4M19 6v4" />
        </svg>
      );
    case "System Setting":
      return (
        <svg {...common}>
          <circle cx="12" cy="12" r="3" />
          <path d="M19.4 15a1.7 1.7 0 00.3 1.8l.1.1a2 2 0 11-2.8 2.8l-.1-.1a1.7 1.7 0 00-1.8-.3 1.7 1.7 0 00-1 1.5V21a2 2 0 11-4 0v-.1a1.7 1.7 0 00-1-1.5 1.7 1.7 0 00-1.8.3l-.1.1a2 2 0 11-2.8-2.8l.1-.1a1.7 1.7 0 00.3-1.8 1.7 1.7 0 00-1.5-1H3a2 2 0 110-4h.1a1.7 1.7 0 001.5-1 1.7 1.7 0 00-.3-1.8l-.1-.1a2 2 0 112.8-2.8l.1.1a1.7 1.7 0 001.8.3H9a1.7 1.7 0 001-1.5V3a2 2 0 114 0v.1a1.7 1.7 0 001 1.5 1.7 1.7 0 001.8-.3l.1-.1a2 2 0 112.8 2.8l-.1.1a1.7 1.7 0 00-.3 1.8V9c.3.6.9 1 1.5 1H21a2 2 0 110 4h-.1a1.7 1.7 0 00-1.5 1z" />
        </svg>
      );
    case "Automation Lead Flow":
      return (
        <svg {...common}>
          <path d="M13 2L3 14h8l-1 8 10-12h-8l1-8z" />
        </svg>
      );
    default:
      return (
        <svg {...common}>
          <path d="M12 2l2.4 7.2L22 12l-7.6 2.8L12 22l-2.4-7.2L2 12l7.6-2.8L12 2z" />
        </svg>
      );
  }
};

const AccountSettings = (props: customProps) => {
  const fromPage = props?.location?.state?.fromPage;
  const location = useLocation();
  const privilegesFromStore = useSelector(selectPrivileges);

  const visibleCards = useMemo(() => {
    const privileges = privilegesFromStore || {};
    return AccountData.filter((item) =>
      canShowAccountSettingCard(item.cardbuttonValue, privileges)
    );
  }, [privilegesFromStore]);

  React.useEffect(() => {
    if (fromPage === "Dashboad") {
      history.push(urls.accountProfilePath);
    }
  }, [location, fromPage]);

  const handleButtonCard = (cardbuttonValue: string) => {
    switch (cardbuttonValue) {
      case "Profile":
        history.push(urls.accountProfilePath);
        break;
      case "Account Status":
        history.push(urls.accountPreferencesPath);
        break;
      case "Security":
        history.push(urls.accountSecurityPath);
        break;
      case "Organization Setup":
      case "Tenant Organizations":
        history.push(urls.accountOrganizationPath);
        break;
      case "Org Integrations":
        history.push(urls.accountIntegrationsPath);
        break;
      case "Audit Logs":
        history.push(urls.auditLogsPath);
        break;
      case "Role":
      case "Roles":
        history.push(urls.rolesPrivilegesViewPath);
        break;
      case "System Setting":
        history.push(urls.accountSystemPath);
        break;
      case "Automation Lead Flow":
      case "Automation":
        history.push(urls.automationLeadFlowPath);
        break;
      default:
        history.push(urls.accountProfilePath);
    }
  };

  return (
    <div className="h-full min-h-0 overflow-auto bg-white p-4 sm:p-6 lg:p-8 [scrollbar-width:thin]">
      <div className="mb-6">
        <h1 className="text-2xl font-bold tracking-tight text-[#1E1B4B] sm:text-[32px] sm:leading-tight">
          Account Settings
        </h1>
        <p className="mt-1.5 text-sm text-[#6B7280] sm:text-[15px]">
          Manage your workspace, security and company preferences.
        </p>
      </div>

      {visibleCards.length === 0 ? (
        <div className="rounded-2xl border border-[#EEF0F5] bg-white p-8 text-center text-sm text-[#6B7280]">
          No settings modules are enabled for your role.
        </div>
      ) : null}

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
        {visibleCards.map((item) => (
          <div
            key={item.id}
            className="flex flex-col rounded-2xl border border-[#EEF0F5] bg-white p-5 shadow-[0_4px_20px_rgba(15,23,42,0.04)] transition hover:shadow-[0_8px_28px_rgba(15,23,42,0.07)]"
          >
            <div className="mb-4 flex items-start gap-3">
              <span
                className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full"
                style={{ backgroundColor: item.theme.iconBg }}
              >
                <CardHeaderIcon
                  type={item.cardbuttonValue}
                  color={item.theme.iconColor}
                />
              </span>
              <div className="min-w-0">
                <h2 className="text-[15px] font-bold text-[#111827]">
                  {item.cardheading}
                </h2>
                <p className="mt-1 text-[12px] leading-relaxed text-[#6B7280]">
                  {item.cardtext}
                </p>
              </div>
            </div>

            <ul className="mb-5 flex flex-1 flex-col gap-3">
              {item.items.map((row) => (
                <li
                  key={row.label}
                  className="flex items-center gap-2.5 text-[13px] text-[#374151]"
                >
                  <span className="text-[#9CA3AF]">
                    <ItemIcon name={row.icon} />
                  </span>
                  {row.label}
                </li>
              ))}
            </ul>

            <button
              type="button"
              onClick={() => handleButtonCard(item.cardbuttonValue)}
              className="mt-auto inline-flex h-11 w-full items-center justify-between rounded-xl px-4 text-sm font-semibold transition"
              style={{
                backgroundColor: item.theme.footerBg,
                color: item.theme.footerText,
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = item.theme.footerHover;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = item.theme.footerBg;
              }}
            >
              <span>{item.carButtonText}</span>
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M9 18l6-6-6-6" />
              </svg>
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};

export default AccountSettings;
